import { useMemo, useState } from 'react';
import {
  DEFAULT_DISPLAY, LEAP_POLICY_LABELS, autoAssignPhotos, calendarPages, describeEventDate, describeColor, imageSlots, resolveDesign, rgb, toCss,
  type EventSet, type ProjectDoc,
} from '@cal/engine';
import { Check, ColorField, Field, Select } from '../ui/controls';
import { assetStore, thumbUrl } from '../assets';
import { ASSET_MIME } from './Canvas';
import { edit, useEditor } from './store';
import { clearFinishedUploads, queuePhotos, useUploads } from './uploads';
import { BrandSection } from './BrandSection';
import { EventSetEditor, CsvImport, LibraryPicker, RefreshFromLibrary } from './EventsUI';

export function ContentStage({ doc }: { doc: ProjectDoc }) {
  const tab = useEditor((s) => s.contentTab);
  const setTab = (t: typeof tab) => useEditor.setState({ contentTab: t });
  return (
    <div>
      <div className="section col">
        <h2>3 · Content</h2>
        <div className="seg" role="tablist" aria-label="Content sections">
          {(['photos', 'captions', 'events', 'brand'] as const).map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} aria-pressed={tab === t} onClick={() => setTab(t)}>{({ photos: 'Photos', captions: 'Captions', events: 'Holidays', brand: 'Brand' } as const)[t]}</button>
          ))}
        </div>
      </div>
      {tab === 'photos' && <Photos doc={doc} />}
      {tab === 'brand' && <BrandSection doc={doc} />}
      {tab === 'captions' && <Captions doc={doc} />}
      {tab === 'events' && <Events doc={doc} />}
    </div>
  );
}

function Photos({ doc }: { doc: ProjectDoc }) {
  const uploads = useUploads((s) => s.items);
  const [over, setOver] = useState(false);
  const pages = calendarPages(doc);
  const used = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of pages) for (const pl of Object.values(doc.photos[p.key] ?? {})) m.set(pl.assetId, [...(m.get(pl.assetId) ?? []), p.kind === 'cover' ? 'Cover' : p.label.slice(0, 3)]);
    return m;
  }, [doc.photos, pages]); // eslint-disable-line react-hooks/exhaustive-deps
  const slotsNeeded = pages.reduce((n, p) => n + imageSlots(resolveDesign(doc, p.key)).length, 0);
  const active = uploads.filter((u) => u.status !== 'done');
  const done = uploads.filter((u) => u.status === 'done').length;
  return (
    <>
      <div className="section col">
        <label className={`dropzone ${over ? 'over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); queuePhotos([...e.dataTransfer.files]); }}>
          <strong>Drop photographs here</strong> or click to choose
          <div className="small">JPG, PNG or WebP · up to 60 MB each · you can keep editing while they upload</div>
          <input type="file" multiple accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { queuePhotos([...(e.target.files ?? [])]); e.target.value = ''; }} />
        </label>
        {uploads.length > 0 && (
          <div className="col" style={{ gap: 4 }}>
            <div className="row small"><span className="grow">{done} of {uploads.filter((u) => u.status !== 'error').length} uploaded</span>{active.length === 0 && <button className="btn sm ghost" onClick={clearFinishedUploads}>Clear list</button>}</div>
            <div className="progress"><div style={{ width: `${(uploads.reduce((s, u) => s + (u.status === 'error' ? 0 : u.progress), 0) / Math.max(1, uploads.filter((u) => u.status !== 'error').length)) * 100}%` }} /></div>
            {uploads.filter((u) => u.status === 'error').map((u) => <div key={u.key} className="notice err small">{u.name}: {u.error}</div>)}
          </div>
        )}
      </div>
      <div className="section col">
        <div className="row">
          <h3 className="grow" style={{ margin: 0 }}>Photographs ({doc.photoPool.length})</h3>
          <button className="btn sm" onClick={() => edit((d) => ({ ...d, photoPool: [...d.photoPool].sort((a, b) => (assetStore.map[a]?.name ?? '').localeCompare(assetStore.map[b]?.name ?? '', undefined, { numeric: true })) }), 'sort')}>Sort by name</button>
        </div>
        <div className="row">
          <button className="btn grow" disabled={!doc.photoPool.length} onClick={() => edit((d) => autoAssignPhotos(d, d.photoPool), 'auto-assign')}>Auto-assign to pages</button>
        </div>
        <p className="small muted">Order: {doc.product.includeCover ? 'Cover, then ' : ''}January → December, using the photos in the order shown ({slotsNeeded} frames). Drag a photo onto a page or thumbnail to place it; drag months in the strip to reorder.</p>
        <div className="pool">
          {doc.photoPool.map((id, i) => (
            <button key={id} className="pool-item" draggable title={assetStore.map[id]?.name}
              style={{ backgroundImage: `url(${thumbUrl(id)})` }}
              onDragStart={(e) => { e.dataTransfer.setData(ASSET_MIME, id); e.dataTransfer.effectAllowed = 'copy'; }}
              onClick={() => { const pg = [...used.entries()].find(([a]) => a === id)?.[1][0]; if (pg) { const p = pages.find((x) => (x.kind === 'cover' ? 'Cover' : x.label.slice(0, 3)) === pg); if (p) useEditor.setState({ page: p.key, view: 'single' }); } }}
              aria-label={`${i + 1}. ${assetStore.map[id]?.name ?? 'photo'}${used.has(id) ? `, used on ${used.get(id)!.join(', ')}` : ', not used'}`}>
              <span className="tag">{i + 1}{used.has(id) ? ` · ${used.get(id)!.join(', ')}` : ''}</span>
            </button>
          ))}
          {active.map((u) => (
            <div key={u.key} className="pool-item" style={{ backgroundImage: u.previewUrl ? `url(${u.previewUrl})` : undefined, opacity: 0.6 }} aria-label={`Uploading ${u.name}`}>
              <div className="prog"><div style={{ width: `${u.progress * 100}%` }} /></div>
            </div>
          ))}
        </div>
        {!doc.photoPool.length && !active.length && <p className="small faint">No photographs yet.</p>}
      </div>
    </>
  );
}

function Captions({ doc }: { doc: ProjectDoc }) {
  const pages = calendarPages(doc);
  const hasCaptionEl = doc.designs.month.elements.some((e) => e.type === 'text' && e.text.includes('{CAPTION}'));
  return (
    <div className="section col">
      {!hasCaptionEl && <div className="notice info small">This template has no caption box. Add {'{CAPTION}'} to a text element to show captions.</div>}
      {pages.map((p) => (
        <Field key={p.key} label={p.label}>
          <input className="input" value={doc.captions[p.key] ?? ''} placeholder="Optional caption" maxLength={500}
            onChange={(e) => { const v = e.target.value; edit((d) => ({ ...d, captions: { ...d.captions, [p.key]: v } }), `caption:${p.key}`); }} />
        </Field>
      ))}
    </div>
  );
}

const KIND_LABEL: Record<EventSet['kind'], string> = { public: 'Public holidays', school: 'School holidays', personal: 'Birthdays & anniversaries', business: 'Business dates', custom: 'Custom dates' };

function Events({ doc }: { doc: ProjectDoc }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [csvFor, setCsvFor] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const [refresh, setRefresh] = useState<string | null>(null);
  const [day, setDay] = useState(`${doc.year}-01-01`);
  const [dayColor, setDayColor] = useState(rgb('#fff3b0'));
  const updateSet = (id: string, fn: (s: EventSet) => EventSet) => edit((d) => ({ ...d, eventSets: d.eventSets.map((s) => (s.id === id ? fn(s) : s)) }), `set:${id}`);
  const newSet = (kind: EventSet['kind']) => {
    const id = `local-${Date.now().toString(36)}`;
    edit((d) => ({ ...d, eventSets: [...d.eventSets, { id, name: kind === 'personal' ? 'Birthdays & anniversaries' : kind === 'business' ? 'Business dates' : 'Custom dates', kind, year: null, source: 'Entered by staff for this project', verification: 'manual', leapPolicy: 'feb28', display: { ...DEFAULT_DISPLAY[kind] }, events: [] }] }), 'new-set');
    setEditing(id);
  };
  return (
    <>
      <div className="section col">
        <div className="row wrap">
          <button className="btn sm primary" onClick={() => setPicker(true)}>Add from library…</button>
          <button className="btn sm" onClick={() => newSet('personal')}>+ Birthdays</button>
          <button className="btn sm" onClick={() => newSet('business')}>+ Business dates</button>
        </div>
        <p className="small muted">Event data is copied into this project so later library changes never silently alter approved artwork. Use “Check for updates” to refresh deliberately.</p>
      </div>
      {doc.eventSets.length === 0 && <div className="section"><p className="small faint">No holidays or events in this project yet.</p></div>}
      {doc.eventSets.map((s) => (
        <div className="section col" key={s.id}>
          <div className="row">
            <div className="col grow" style={{ gap: 2 }}>
              <strong>{s.name}</strong>
              <span className="small faint">{KIND_LABEL[s.kind]}{s.year ? ` · ${s.year}` : ' · every year'} · {s.events.length} entries</span>
            </div>
            <span className={`badge ${s.verification === 'verified' ? 'ok' : s.verification === 'manual' ? 'info' : 'warn'}`}>{s.verification === 'verified' ? 'Verified' : s.verification === 'manual' ? 'Manual' : 'Unverified'}</span>
          </div>
          {s.year !== null && s.year !== doc.year && <div className="notice err small">This data is for {s.year}, but the calendar is {doc.year}. Remove it and add the {doc.year} set.</div>}
          {s.verification !== 'verified' && s.kind !== 'personal' && s.kind !== 'business' && <div className="notice warn small">{s.verificationNote ?? 'Not verified against an official source.'}</div>}
          <details className="adv">
            <summary>Source & display</summary>
            <div className="col">
              <p className="small muted" style={{ margin: 0 }}>{s.source}{s.sourceUrl && <> — <a href={s.sourceUrl} target="_blank" rel="noreferrer noopener">source</a></>}{s.verifiedAt && ` · verified ${s.verifiedAt}`}</p>
              <Check label="Show on calendar" checked={s.display.visible} onChange={(v) => updateSet(s.id, (x) => ({ ...x, display: { ...x.display, visible: v } }))} />
              <div className="row wrap" style={{ gap: 12 }}>
                <Check label="Label in day" checked={s.display.showLabel} onChange={(v) => updateSet(s.id, (x) => ({ ...x, display: { ...x.display, showLabel: v } }))} />
                <Check label="Shade day" checked={s.display.shade} onChange={(v) => updateSet(s.id, (x) => ({ ...x, display: { ...x.display, shade: v } }))} />
                <Check label="List under the month grid" checked={s.display.legend} onChange={(v) => updateSet(s.id, (x) => ({ ...x, display: { ...x.display, legend: v } }))} />
              </div>
              <div className="grid2">
                <ColorField label="Text colour" value={s.display.textColor} onChange={(c) => c && updateSet(s.id, (x) => ({ ...x, display: { ...x.display, textColor: c } }))} brandColors={doc.brand.colors} />
                <ColorField label="Shading" value={s.display.background} onChange={(c) => c && updateSet(s.id, (x) => ({ ...x, display: { ...x.display, background: c } }))} brandColors={doc.brand.colors} />
              </div>
              {s.events.some((e) => e.type === 'recurring' && e.month === 2 && e.day === 29) && (
                <Select label="29 February in non-leap years" value={s.leapPolicy} onChange={(v) => updateSet(s.id, (x) => ({ ...x, leapPolicy: v }))} options={Object.entries(LEAP_POLICY_LABELS).map(([value, label]) => ({ value: value as EventSet['leapPolicy'], label }))} />
              )}
            </div>
          </details>
          <div className="row wrap">
            <button className="btn sm" onClick={() => setEditing(s.id)}>Edit dates</button>
            <button className="btn sm" onClick={() => setCsvFor(s.id)}>Import CSV</button>
            {s.libraryId && <button className="btn sm" onClick={() => setRefresh(s.id)}>Check for updates</button>}
            <span className="spacer" />
            <button className="btn sm ghost danger" onClick={() => edit((d) => ({ ...d, eventSets: d.eventSets.filter((x) => x.id !== s.id) }), 'remove-set')}>Remove</button>
          </div>
          {s.events.length > 0 && s.events.length <= 6 && <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>{s.events.map((e) => <li key={e.id}>{e.label} — {describeEventDate(e)}</li>)}</ul>}
        </div>
      ))}
      <div className="section col">
        <h3>Individual day colours</h3>
        <p className="small muted">Highest priority: overrides holiday and weekend shading for one date.</p>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <Field label="Date"><input className="input" type="date" value={day} min={`${doc.year}-01-01`} max={`${doc.year}-12-31`} onChange={(e) => setDay(e.target.value)} /></Field>
          <div style={{ width: 120 }}><ColorField label="Colour" value={dayColor} onChange={(c) => c && setDayColor(c)} brandColors={doc.brand.colors} /></div>
          <button className="btn sm" disabled={!/^\d{4}-\d\d-\d\d$/.test(day) || +day.slice(0, 4) !== doc.year} onClick={() => edit((d) => ({ ...d, cellOverrides: { ...d.cellOverrides, [day]: { fill: dayColor } } }), 'cell')}>Set</button>
        </div>
        {Object.entries(doc.cellOverrides).sort().map(([k, v]) => (
          <div key={k} className="row small"><span className="color-swatch" style={{ width: 16, height: 16, background: toCss(v.fill) }} /><span className="grow mono">{k} · {describeColor(v.fill)}</span>
            <button className="btn sm ghost" onClick={() => edit((d) => { const c = { ...d.cellOverrides }; delete c[k]; return { ...d, cellOverrides: c }; }, 'cell')}>Remove</button></div>
        ))}
      </div>
      {editing && <EventSetEditor doc={doc} setId={editing} onClose={() => setEditing(null)} />}
      {csvFor && <CsvImport doc={doc} setId={csvFor} onClose={() => setCsvFor(null)} />}
      {picker && <LibraryPicker doc={doc} onClose={() => setPicker(false)} />}
      {refresh && <RefreshFromLibrary doc={doc} setId={refresh} onClose={() => setRefresh(null)} />}
    </>
  );
}
